import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@amzn/awsui-components-react/polaris/box';
import Button from '@amzn/awsui-components-react/polaris/button';
import Calendar from '@amzn/awsui-components-react/polaris/calendar';
import Popover from '@amzn/awsui-components-react/polaris/popover';
import SpaceBetween from '@amzn/awsui-components-react/polaris/space-between';
import { i18nKeys } from '@/src/utils/i18n.utils';
import { Header } from '@amzn/awsui-components-react';
import { getLanguageCodeSafe } from '@/src/utils/locale.utils';
import {
  PRIMARY_STATUSES,
  SECONDARY_STATUSES,
  STATUS_DESCRIPTION_KEYS,
  STATUS_LABEL_KEYS,
} from './labStatus.constants';
import styles from './LabCalendar.module.scss';

export interface CalendarHeaderProps {
  displayedMonth: Date;
  monthYearLabel: string;
  onToday: () => void;
  onMonthSelect: (year: number, month: number) => void;
}

const CalendarHeader: React.FC<CalendarHeaderProps> = ({ displayedMonth, monthYearLabel, onToday, onMonthSelect }) => {
  const { t, i18n } = useTranslation();
  const languageCode: string = getLanguageCodeSafe(i18n.language);

  const monthIndex = displayedMonth.getMonth();
  const year = displayedMonth.getFullYear();

  const now = new Date();
  const isCurrentMonth = monthIndex === now.getMonth() && year === now.getFullYear();

  // Format value for the month-granularity Calendar: "YYYY-MM"
  const calendarValue = `${year}-${String(monthIndex + 1).padStart(2, '0')}`;

  const handleCalendarChange = (value: string) => {
    const [selectedYear, selectedMonth] = value.split('-').map(Number);
    if (!isNaN(selectedYear) && !isNaN(selectedMonth)) {
      onMonthSelect(selectedYear, selectedMonth - 1);
    }
  };

  // Shift the displayed month by `delta` months. new Date() normalizes month
  // under/overflow, so December->January and January->December roll the year.
  const handleShiftMonth = (delta: number) => {
    const shifted = new Date(year, monthIndex + delta, 1);
    onMonthSelect(shifted.getFullYear(), shifted.getMonth());
  };

  // Same order as the day cells so the legend reads top-to-bottom like the grid.
  const statusInfo = (
    // Wrapper only exists to enlarge the Popover's built-in header (see .statusInfoPopover).
    <div className={styles.statusInfoPopover}>
      <Popover
        triggerType="custom"
        size="large"
        header={t(i18nKeys.labDashboard.calendar.statusInfo.header)}
        content={
          <SpaceBetween direction="vertical" size="s">
            <Box variant="p" color="text-body-secondary">
              {t(i18nKeys.labDashboard.calendar.statusInfo.description)}
            </Box>
            {[...PRIMARY_STATUSES, ...SECONDARY_STATUSES].map((status) => (
              <div key={status} data-testid={`calendar-header__status-info-${status}`}>
                <Box variant="awsui-key-label">{t(STATUS_LABEL_KEYS[status])}</Box>
                <Box>{t(STATUS_DESCRIPTION_KEYS[status])}</Box>
              </div>
            ))}
          </SpaceBetween>
        }
      >
        <Button
          iconName="status-info"
          ariaLabel={t(i18nKeys.labDashboard.calendar.statusInfo.ariaLabel)}
          data-testid="calendar-header__status-info-btn"
        />
      </Popover>
    </div>
  );

  return (
    <SpaceBetween direction="vertical" size="s">
      <Header variant="h1">{t(i18nKeys.labDashboard.calendar.header)}</Header>
      <div className={styles.headerControlsRow}>
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          <Box variant="h2" padding={{ top: 'n' }} data-testid="calendar-month-year-label">
            {monthYearLabel}
          </Box>
          <Button
            iconName="angle-left"
            variant="normal"
            onClick={() => handleShiftMonth(-1)}
            ariaLabel={t(i18nKeys.labDashboard.calendar.previousMonth)}
            data-testid="calendar-header__prev-month-btn"
          />
          <Popover
            triggerType="custom"
            content={
              <Calendar
                value={calendarValue}
                granularity="month"
                locale={languageCode}
                onChange={({ detail }) => handleCalendarChange(detail.value)}
                ariaLabel={t(i18nKeys.labDashboard.calendar.monthPickerLabel)}
                i18nStrings={{
                  nextMonthAriaLabel: t(i18nKeys.labDashboard.calendar.nextMonth),
                  previousMonthAriaLabel: t(i18nKeys.labDashboard.calendar.previousMonth),
                }}
              />
            }
          >
            <Button
              iconName="calendar"
              variant="normal"
              ariaLabel={t(i18nKeys.labDashboard.calendar.monthPickerLabel)}
              data-testid="calendar-header__date-picker"
            />
          </Popover>
          <Button
            iconName="angle-right"
            variant="normal"
            onClick={() => handleShiftMonth(1)}
            ariaLabel={t(i18nKeys.labDashboard.calendar.nextMonth)}
            data-testid="calendar-header__next-month-btn"
          />
          {!isCurrentMonth && (
            <Button variant="primary" onClick={onToday} data-testid="calendar-header__view-today-btn">
              {t(i18nKeys.labDashboard.calendar.viewCurrentMonth)}
            </Button>
          )}
        </SpaceBetween>
        {statusInfo}
      </div>
    </SpaceBetween>
  );
};

export default CalendarHeader;
